import {
  BehaviorSubject,
  Observable,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  Component,
  DestroyRef,
  inject,
  Input,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import {
  createAlert,
  deleteAlert,
  editAlert,
  loadFeedAlerts,
} from 'src/app/core/actions/feed.actions';
import {
  selectFeed,
  selectFeedAlerts,
  selectFeedAlertsLoading,
  selectUserAlertsCount,
} from 'src/app/core/reducers';

import { Alert } from 'src/app/core/models/alert.models';
import { AlertEditorComponent } from '../../alert-editor/alert-editor.component';
import { AppState } from 'src/app/reducers';
import { Feed } from 'src/app/core/models/core.models';
import { ToolbarButton } from '../../../models/toolbar.models';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { objectChanged } from 'src/app/core/actions/core.actions';
import { ConfirmDialogComponent } from '../../popups/confirm-dialog/confirm-dialog.component';
import {
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
} from '@sui/dialog';

@Component({
  selector: 'app-feed-alerts',
  templateUrl: './feed-alerts.component.html',
  styleUrls: ['./feed-alerts.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FeedAlertsComponent {
  @ViewChild('alertEditor', { static: false })
  alertEditor!: AlertEditorComponent;
  searchString: string;
  alerts$: Observable<Array<Alert>>;
  editMode: boolean = false;
  createMode: boolean = false;
  alertsCount$: Observable<number>;
  alertsLoading$: Observable<boolean>;
  alertsCount: any;
  feed$: Observable<Feed>;
  feedId: string;
  loading: boolean = false;
  selectedAlert: Alert;

  buttons$ = new BehaviorSubject<ToolbarButton[]>([]);

  protected destroyRef = inject(DestroyRef);
  private actions$ = inject(Actions);
  private dialogService = inject(DialogService);

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
  ) {
    this.feed$ = this.store.pipe(select(selectFeed));
    this.alerts$ = this.store.pipe(select(selectFeedAlerts));
    this.alertsCount$ = this.store.pipe(select(selectUserAlertsCount));
    this.alertsLoading$ = this.store.pipe(select(selectFeedAlertsLoading));
  }

  ngOnInit() {
    // 1) Load alerts once if none are present
    this.store
      .pipe(
        select(selectFeed),
        withLatestFrom(this.alerts$),
        take(1),
        tap(([feed, alerts]) => {
          if (!alerts?.length) {
            this.store.dispatch(loadFeedAlerts({ feedId: feed.id }));
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // 2) Keep alertsCount in sync
    this.alerts$
      .pipe(
        map((arr) => arr.length),
        distinctUntilChanged(),
        tap((count) => (this.alertsCount = count)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // 3) Keep feedId in sync
    this.feed$
      .pipe(
        map((feed) => feed.id),
        distinctUntilChanged(),
        tap((id) => (this.feedId = id)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.updateToolbarButtons();

    this.actions$
      .pipe(
        ofType(objectChanged),
        withLatestFrom(this.store.select(selectFeed)),
        tap(([, feed]) => {
          if (!feed) {
            alert('not feed');

            return;
          }
          this.store.dispatch(loadFeedAlerts({ feedId: feed.id }));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    // Now Angular has guaranteed that `alertEditor` is non-null
    this.hookAlertEditor();
  }

  private hookAlertEditor() {
    if (!this.alertEditor) {
      return;
    }
    this.alertEditor.saving$
      .pipe(
        distinctUntilChanged(),
        tap((s) => (this.loading = s)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.alertEditor.success$
      .pipe(
        filter((s) => s === true),
        tap(() => {
          this.editMode = false;
          this.createMode = false;
          this.updateToolbarButtons();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  updateToolbarButtons() {
    const buttons: ToolbarButton[] = [
      {
        type: 'button',
        variant: 'primary',
        label: this.translateService.instant('feed-alerts.button.create-alert'),
        action: this.openAlertCreate.bind(this),
        loading: this.loading,
        shouldDisplay: !this.editMode && !this.createMode,
      },
      {
        type: 'button',
        variant: 'ghost',
        label: this.translateService.instant('btn.cancel'),
        action: () => {
          this.editMode = false;
          this.createMode = false;
          this.updateToolbarButtons();
        },
        shouldDisplay: this.editMode || this.createMode,
      },
      {
        type: 'button',
        variant: 'primary',
        label: this.translateService.instant('btn.save'),
        action: this.alertEdit.bind(this),
        loading: this.loading,
        shouldDisplay: this.editMode,
      },
      {
        type: 'button',
        variant: 'primary',
        label: this.translateService.instant('btn.create'),
        action: this.alertCreate.bind(this),
        shouldDisplay: this.createMode,
      },
    ];

    this.buttons$.next(buttons);
  }

  openAlertEdit(alert: Alert) {
    this.editMode = true;
    this.selectedAlert = alert;
    setTimeout(() => {
      this.hookAlertEditor();
    }, 0);
    this.updateToolbarButtons();
  }

  openAlertCreate() {
    this.createMode = true;
    setTimeout(() => {
      this.hookAlertEditor();
    }, 0);
    this.updateToolbarButtons();
  }

  alertCreate() {
    this.alertEditor.submit();
    this.updateToolbarButtons();
  }

  alertEdit() {
    this.alertEditor.submit();
    this.updateToolbarButtons();
  }

  alertDelete(alert: Alert) {
    const title = this.translateService.instant(
      'feed-alerts.alert-delete-confirmation-title',
    );

    const message = this.translateService.instant(
      'feed-alerts.alert-delete-confirmation',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          title,
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.store.dispatch(deleteAlert({ alertId: alert.id }));
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }
}
